/**
 * AbteilungPage — /abteilung/<slug>/<tab>
 *
 * tab = dashboard | workflow | beendet. Der Kopf (Titel/Unterzeile) kommt aus
 * dem App-Header; hier steht nur der Inhalt.
 *  - Dashboard: Layout der Abteilung, read-only
 *  - Workflow:  pipeline pb → AnfragenPage, bk → BkAnfragenPage, custom → Kanban
 *  - Beendet:   Archiv der pb-/bk-Abteilung (BeendeteAnfragenPage)
 */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionTitle } from '../../components/ui';
import { useMyDepartments } from '../../lib/departments';
import { getThemeColors } from '../../lib/theme';
import { AnfragenPage } from '../anfragen/AnfragenPage';
import { BkAnfragenPage } from '../anfragen_bk/BkAnfragenPage';
import { BeendeteAnfragenPage } from '../anfragen_beendet/BeendeteAnfragenPage';
import { AbteilungDashboardView } from './AbteilungDashboardView';
import { AbteilungWorkflowCustom } from './AbteilungWorkflowCustom';
import type { Density } from '../../types';
import type { PageId } from '../../lib/route';

export type AbteilungTab = 'dashboard' | 'workflow' | 'beendet';

export function parseAbteilungTab(tab: string | null): AbteilungTab {
  return tab === 'workflow' || tab === 'beendet' ? tab : 'dashboard';
}

interface Props {
  slug: string | null;
  tab: string | null;
  dark: boolean;
  density: Density;
  setPage: (page: string) => void;
  navigate: (page: PageId, sub?: string | null, tab?: string | null, extra?: string | null, opts?: { replace?: boolean }) => void;
}

export function AbteilungPage({ slug, tab, dark, density, setPage, navigate }: Props) {
  const { t: tr } = useTranslation();
  const t = getThemeColors(dark);
  const { departments, loading } = useMyDepartments();
  const activeTab = parseAbteilungTab(tab);
  const dept = slug ? departments.find(d => d.slug === slug) : undefined;

  // /abteilung ohne Kürzel → erste eigene Abteilung.
  useEffect(() => {
    if (!slug && departments.length > 0) navigate('abteilung', departments[0].slug, 'dashboard', null, { replace: true });
  }, [slug, departments, navigate]);

  if (loading && !dept) {
    return <div style={{ padding: 24, color: t.muted, fontSize: 13 }}>{tr('departments.page.loading')}</div>;
  }

  if (!dept) {
    return (
      <Card dark={dark} style={{ padding: 24, maxWidth: 520 }}>
        <SectionTitle dark={dark} title={tr('departments.page.not_found_title')} subtitle={tr('departments.page.not_found_text')} />
      </Card>
    );
  }

  if (activeTab === 'dashboard') {
    return <AbteilungDashboardView slug={dept.slug} dark={dark} density={density} setPage={setPage} />;
  }

  if (activeTab === 'beendet') {
    if (dept.pipeline === 'custom') {
      return <AbteilungWorkflowCustom slug={dept.slug} dark={dark} />;
    }
    return <BeendeteAnfragenPage scope={dept.pipeline} dark={dark} density={density} />;
  }

  if (dept.pipeline === 'pb') {
    return <AnfragenPage dark={dark} density={density} onOpenPb={(userId) => navigate('pflege', String(userId))} />;
  }
  if (dept.pipeline === 'bk') {
    return <BkAnfragenPage dark={dark} density={density} />;
  }
  return <AbteilungWorkflowCustom slug={dept.slug} dark={dark} />;
}
